import type {
  FriendsResponse,
  ReaderPresence,
  ServerEvent,
} from '../types/book';

export type PresenceAction = 'open' | 'update' | 'close';
export type FriendActivityResponse = {
  byHash: Record<string, Record<string, ReaderPresence>>;
  recentByHash?: Record<string, Record<string, ReaderPresence>>;
};
export type FriendActivityOptions = {
  includeRecent?: boolean;
  recentMs?: number;
};

const jsonHeaders = {
  'Content-Type': 'application/json',
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.error || `Request failed with ${response.status}`);
  }
  return data as T;
};

export const fetchFriends = async (): Promise<FriendsResponse> => {
  const response = await fetch('/api/friends', {
    credentials: 'include',
  });
  return parseJsonResponse<FriendsResponse>(response);
};

export const followFriend = async (email: string): Promise<FriendsResponse> => {
  const response = await fetch('/api/friends/follow', {
    credentials: 'include',
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ email }),
  });
  return parseJsonResponse<FriendsResponse>(response);
};

export const unfollowFriend = async (email: string): Promise<FriendsResponse> => {
  const response = await fetch('/api/friends/unfollow', {
    credentials: 'include',
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ email }),
  });
  return parseJsonResponse<FriendsResponse>(response);
};

export const fetchFriendActivity = async (
  hashes: string[],
  options: FriendActivityOptions = {},
): Promise<FriendActivityResponse> => {
  const response = await fetch('/api/friends/activity', {
    credentials: 'include',
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({
      hashes,
      ...(options.includeRecent ? { includeRecent: true } : {}),
      ...(typeof options.recentMs === 'number' ? { recentMs: options.recentMs } : {}),
    }),
  });
  return parseJsonResponse<FriendActivityResponse>(response);
};

export const sendReaderPresence = async (
  presence: ReaderPresence & { action: PresenceAction },
  options: { keepalive?: boolean } = {},
): Promise<void> => {
  const body = JSON.stringify(presence);
  if (options.keepalive && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' });
    navigator.sendBeacon('/api/reader_presence', blob);
    return;
  }

  const response = await fetch('/api/reader_presence', {
    credentials: 'include',
    method: 'POST',
    headers: jsonHeaders,
    body,
    keepalive: options.keepalive,
  });
  await parseJsonResponse(response);
};

export const dispatchServerEvent = (event: ServerEvent): void => {
  window.dispatchEvent(new CustomEvent<ServerEvent>('wereader:server-event', { detail: event }));
};
